/**
 * Vendor-wise month-on-month invoice status.
 *
 * One row per vendor, one column group per month, drilling vendor → month →
 * status → invoice list. The month-on-month delta is against the previous month
 * in the same row, so a vendor whose trend changed direction is visible without
 * reading the absolute levels.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { STAGE_LABEL, day, int, money, num, useIptTheme } from './iptTheme';
import { Def, Empty, Panel, Sample, StageChip } from './IptPrimitives';

const COL_TONE = { received: 'primary', certified: 'primary', approved: 'green',
  paid: 'green', blocked: 'amber', disputed: 'red', returned: 'accent' };
const STAGE_OF = { received: null, certified: 'CERTIFIED', approved: 'APPROVED',
  paid: 'PAID', blocked: 'ON_HOLD', disputed: 'DISPUTED', returned: 'RETURNED' };

export const VendorMonthKpi = ({ projectId, basis = 'receipt' }) => {
  const T = useIptTheme();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState('received');
  const [openVendor, setOpenVendor] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [showValue, setShowValue] = useState(false);

  useEffect(() => {
    setD(null);
    const sp = new URLSearchParams({ basis });
    if (projectId) sp.set('project_id', projectId);
    api.get(`/ipt/analytics/vendor-month?${sp}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [projectId, basis]);

  useEffect(() => {
    if (!openVendor || !openMonth) { setCohort(null); return; }
    const sp = new URLSearchParams({ month: openMonth, month_basis: basis,
      vendor_name: openVendor });
    if (projectId) sp.set('project_id', projectId);
    api.get(`/ipt/analytics/cohort?${sp}`).then((r) => setCohort(r.data)).catch(() => setCohort(null));
  }, [openVendor, openMonth, basis, projectId]);

  const months = d?.months || [];
  const cur = 'INR';

  const maxCell = useMemo(() => Math.max(
    1, ...(d?.rows || []).flatMap((r) => r.months.map(
      (c) => (showValue ? c[`${metric}Value`] : c[metric]) || 0)),
  ), [d, metric, showValue]);

  if (!d) {
    return (
      <Panel T={T} title="Vendor-wise month-on-month invoice status" subtitle="loading…"
        testId="vm-kpi-loading">
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: T.primary }} />
      </Panel>
    );
  }

  return (
    <Panel T={T} testId="vm-kpi" title="Vendor-wise month-on-month invoice status"
      subtitle={`${int(d.rows.length)} vendors × ${int(months.length)} months · `
        + (basis === 'status' ? 'by month the status was reached' : 'by receipt month')}
      right={(
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {(d.columns || []).map((c) => (
              <button key={c.key} type="button" onClick={() => setMetric(c.key)}
                data-testid={`vm-metric-${c.key}`}
                className="px-2 py-0.5 rounded text-[10px] font-semibold"
                style={metric === c.key
                  ? { background: T.primary, color: '#fff' }
                  : { color: T.body, border: `1px solid ${T.border}` }}>
                {c.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowValue(!showValue)}
            data-testid="vm-toggle-value"
            className="px-2 py-0.5 rounded text-[10px] font-semibold"
            style={{ color: showValue ? T.primary : T.muted, border: `1px solid ${T.border}` }}>
            {showValue ? 'value' : 'count'}
          </button>
          <Def T={T} testId="vm-def" title="Vendor-wise month-on-month status"
            body={d.definition} sample={d.band?.note} />
        </div>
      )}>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="vm-table">
          <thead>
            <tr style={{ color: T.muted, background: T.surfaceAlt }}>
              <th className="py-2 px-2 text-left font-semibold sticky left-0"
                style={{ background: T.surfaceAlt }}>Vendor</th>
              {months.map((m) => (
                <th key={m} className="py-2 px-1.5 text-right font-semibold whitespace-nowrap">
                  {m.slice(2)}
                </th>
              ))}
              <th className="py-2 px-2 text-right font-semibold">Total</th>
              <th className="py-2 px-2 text-right font-semibold">MoM</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const latest = r.latest || {};
              const delta = (latest.mom || {})[metric];
              const open = openVendor === r.vendor_name;
              return (
                <Fragment key={r.vendor_name}>
                  <tr data-testid={`vm-row-${r.vendor_name}`}
                    style={{ borderTop: `1px solid ${T.border}`,
                             background: open ? T.tint(T.primary, '10') : 'transparent' }}>
                    <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 cursor-pointer"
                      onClick={() => { setOpenVendor(open ? null : r.vendor_name); setOpenMonth(null); }}
                      style={{ background: open ? T.surfaceAlt : T.surface, color: T.ink }}>
                      <span className="inline-flex items-center gap-1">
                        {open ? <ChevronDown className="w-3 h-3" />
                          : <ChevronRight className="w-3 h-3" />}
                        <span className="truncate max-w-[170px] font-semibold">{r.vendor_name}</span>
                        {r.imported > 0 && (
                          <span className="px-1 rounded text-[8px] font-bold uppercase"
                            style={{ background: T.tint(T.amber, '1A'), color: T.amber }}>
                            {int(r.imported)} imp
                          </span>
                        )}
                      </span>
                    </td>
                    {r.months.map((c) => {
                      const v = (showValue ? c[`${metric}Value`] : c[metric]) || 0;
                      const intensity = v / maxCell;
                      const sel = open && openMonth === c.month;
                      return (
                        <td key={c.month} data-testid={`vm-cell-${r.vendor_name}-${c.month}`}
                          onClick={() => { setOpenVendor(r.vendor_name); setOpenMonth(c.month); }}
                          className="py-1.5 px-1.5 text-right tabular-nums cursor-pointer"
                          style={{
                            background: v
                              ? T.tint(T.primary, intensity > 0.6 ? '44'
                                : intensity > 0.3 ? '2A' : '14')
                              : 'transparent',
                            outline: sel ? `1px solid ${T.primary}` : 'none',
                            color: v ? T.ink : T.muted,
                          }}>
                          {v ? (showValue ? money(v, cur) : int(v)) : '·'}
                        </td>
                      );
                    })}
                    <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                      style={{ color: T.ink }}>
                      {showValue ? money(r.totalsValue[metric], cur) : int(r.totals[metric])}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums"
                      data-testid={`vm-mom-${r.vendor_name}`}>
                      {delta === null || delta === undefined ? (
                        <span style={{ color: T.muted }}>—</span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 font-semibold"
                          style={{ color: delta > 0 ? T.green : (delta < 0 ? T.red : T.muted) }}>
                          {delta > 0 ? <TrendingUp className="w-3 h-3" />
                            : delta < 0 ? <TrendingDown className="w-3 h-3" />
                              : <Minus className="w-3 h-3" />}
                          {delta > 0 ? `+${int(delta)}` : int(delta)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr data-testid={`vm-drill-${r.vendor_name}`}>
                      <td colSpan={months.length + 3} className="p-2"
                        style={{ background: T.surfaceAlt }}>
                        <div className="flex items-center gap-1.5 flex-wrap mb-2">
                          <span className="text-[10px]" style={{ color: T.muted }}>
                            status mix for {r.vendor_name}
                            {openMonth ? ` in ${openMonth}` : ' across all months'}:
                          </span>
                          {(d.columns || []).map((c) => {
                            const cellRow = openMonth
                              ? r.months.find((m) => m.month === openMonth) : null;
                            const n = cellRow ? cellRow[c.key] : r.totals[c.key];
                            if (!n) return null;
                            return (
                              <span key={c.key} className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                data-testid={`vm-status-${r.vendor_name}-${c.key}`}
                                style={{ background: T.tint(T.primary, '14'), color: T.ink }}>
                                {c.label} {int(n)}
                              </span>
                            );
                          })}
                          {openMonth && (
                            <button type="button" onClick={() => setOpenMonth(null)}
                              data-testid="vm-clear-month"
                              className="text-[10px] underline ml-1" style={{ color: T.primary }}>
                              all months
                            </button>
                          )}
                        </div>
                        {openMonth && !cohort && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: T.primary }} />
                        )}
                        {openMonth && cohort && (
                          <table className="w-full text-[10.5px]"
                            data-testid={`vm-cohort-${r.vendor_name}`}>
                            <thead>
                              <tr style={{ color: T.muted }}>
                                {['Invoice', 'Stage', 'Received', 'Due', 'Beyond', 'Net payable']
                                  .map((h, i) => (
                                    <th key={h} className={`py-1 px-1.5 font-semibold ${i > 1 ? 'text-right' : 'text-left'}`}>
                                      {h}
                                    </th>
                                  ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(cohort.rows || []).slice(0, 25).map((x) => (
                                <tr key={x.invoice_id} data-testid={`vm-invoice-${x.invoice_no}`}
                                  onClick={() => navigate(
                                    `/project-controls/cost/invoices/${x.invoice_id}?tab=lifecycle`)}
                                  className="cursor-pointer"
                                  style={{ borderTop: `1px solid ${T.border}` }}>
                                  <td className="py-1 px-1.5 font-semibold"
                                    style={{ color: T.primary }}>{x.invoice_no}</td>
                                  <td className="py-1 px-1.5">
                                    <StageChip stage={x.current_stage} T={T} />
                                  </td>
                                  <td className="py-1 px-1.5 text-right tabular-nums"
                                    style={{ color: T.body }}>{day(x.receipt_date)}</td>
                                  <td className="py-1 px-1.5 text-right tabular-nums"
                                    style={{ color: T.body }}>{day(x.due_date)}</td>
                                  <td className="py-1 px-1.5 text-right tabular-nums"
                                    style={{ color: x.metrics.days_beyond_terms > 0 ? T.red : T.muted }}>
                                    {x.metrics.days_beyond_terms > 0
                                      ? num(x.metrics.days_beyond_terms, 0) : '—'}
                                  </td>
                                  <td className="py-1 px-1.5 text-right tabular-nums font-semibold"
                                    style={{ color: T.ink }}>{money(x.net_payable, cur)}</td>
                                </tr>
                              ))}
                              {!(cohort.rows || []).length && (
                                <tr><td colSpan={6} className="py-2 text-[10px]"
                                  style={{ color: T.muted }}>
                                  No invoices for this vendor in {openMonth} on this basis.
                                </td></tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!d.rows.length && <tr><td colSpan={3}><Empty T={T}>No vendors in scope.</Empty></td></tr>}
          </tbody>
        </table>
      </div>
      <Sample T={T} testId="vm-sample" text={d.definition} />
    </Panel>
  );
};

export default VendorMonthKpi;
